import { useMemo } from 'react';
import { gql } from '@apollo/client';
import {
  ContactInteractionMessageTemplate,
  Query,
  QueryContactInteractionMessageTemplatesArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { CORE_PAGE_INFO_FIELDS } from 'src/components/providers/apollo-provider-provider';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useInfiniteListQuery } from 'src/hooks/use-infinite-list-query';

const CONTACT_INTERACTION_MESSAGE_TEMPLATE_QUERY = gql`
  ${CORE_PAGE_INFO_FIELDS}
  query GetContactInteractionMessageTemplates(
    $companyId: Int!
    $first: Int
    $after: String
    $before: String
    $last: Int
    $where: ContactInteractionMessageTemplateWhereInput
  ) {
    contactInteractionMessageTemplates(
      companyId: $companyId
      first: $first
      after: $after
      before: $before
      last: $last
      where: $where
    ) {
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
      edges {
        cursor
        node {
          id
          name
          content
          messagePreview
          language
          shareWith
          department {
            id
            name
          }
          company {
            id
          }
          createdAt
          createdBy {
            id
          }
          updatedAt
          updatedBy {
            id
          }
        }
      }
    }
  }
`;

const PAGE_SIZE = 15;

type UseContactInteractionMessageTemplatesQueryProps = {
  skip?: boolean;
  where?: QueryContactInteractionMessageTemplatesArgs['where'];
};

export const useContactInteractionMessageTemplatesQuery = ({
  skip,
  where,
}: UseContactInteractionMessageTemplatesQueryProps) => {
  const { id: institutionId } = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();

  const variables = useMemo(() => {
    const baseVariables = {
      first: PAGE_SIZE,
      companyId: institutionId,
      where,
    };

    return { ...baseVariables };
  }, [institutionId, where]);

  return useInfiniteListQuery<
    Pick<Query, 'contactInteractionMessageTemplates'>,
    Partial<QueryContactInteractionMessageTemplatesArgs>,
    ContactInteractionMessageTemplate
  >({
    queryOptions: {
      query: CONTACT_INTERACTION_MESSAGE_TEMPLATE_QUERY,
      getNodeIdCallback: (template) => template?.id,
      notifyOnNetworkStatusChange: true,
      skip,
      variables,
      getEdgesCallback: (data) => data?.contactInteractionMessageTemplates.edges ?? [],
      getPageInfoCallback: (data) => data?.contactInteractionMessageTemplates.pageInfo,
      onError: handleGraphQLError,
      queryPageSize: PAGE_SIZE,
    },
  });
};
